(() => {
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let refreshPromise = null;
  let reconcilePromise = null;
  let drawerObserver = null;
  let cartActionRunning = false;
  let noteSaveTimer = null;

  function getCartDrawer() {
    return document.querySelector('[data-quick-cart]');
  }

  function url(path = '') {
    return `${ROOT}${path}`.replace(/\/{2,}/g, '/');
  }

  function isServiceItem(item) {
    return item?.handle === 'unbegun-personalizer' || item?.product_handle === 'unbegun-personalizer';
  }

  async function getCartJson() {
    const response = await fetch(`${url('cart.js')}?_=${Date.now()}`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Winkelwagen kon niet worden geladen.');
    return response.json();
  }

  function updateCartCount(cart) {
    const visibleCount = (cart?.items || []).reduce((sum, item) => {
      return sum + (isServiceItem(item) ? 0 : Number(item.quantity || 0));
    }, 0);
    document.querySelectorAll('.js-cart-count').forEach((count) => {
      count.textContent = String(visibleCount);
    });
  }

  async function postCartUpdate(updates, note) {
    const payload = { updates };
    if (typeof note === 'string') payload.note = note;

    const response = await fetch(url('cart/update.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result?.description || 'Winkelwagen bijwerken is niet gelukt.');
    updateCartCount(result);
    return result;
  }

  const SERVICE_LINK_KEYS = ['_Personalisatie koppeling', '_Cadeau koppeling', '_Toetsenbord koppeling'];

  function serviceLink(item) {
    for (const key of SERVICE_LINK_KEYS) {
      const value = item?.properties?.[key];
      if (value) return { key, value };
    }
    return null;
  }

  function groupDesiredQuantities(cart) {
    const desiredByLink = new Map();
    (cart.items || []).forEach((item) => {
      if (isServiceItem(item)) return;
      SERVICE_LINK_KEYS.forEach((key) => {
        const link = item.properties?.[key];
        if (link) desiredByLink.set(link, (desiredByLink.get(link) || 0) + Number(item.quantity || 0));
      });
    });
    return desiredByLink;
  }

  async function synchronizeServicesFromCurrentCart() {
    const cart = await getCartJson();
    const desiredByLink = groupDesiredQuantities(cart);
    const updates = {};
    const firstServiceSeen = new Set();

    (cart.items || []).forEach((item) => {
      if (!isServiceItem(item)) return;
      const linked = serviceLink(item);
      if (!linked) return;
      const desired = desiredByLink.get(linked.value) || 0;
      const unique = `${linked.key}:${linked.value}`;
      if (firstServiceSeen.has(unique)) updates[item.key] = 0;
      else {
        firstServiceSeen.add(unique);
        if (Number(item.quantity || 0) !== desired) updates[item.key] = desired;
      }
    });

    if (Object.keys(updates).length) await postCartUpdate(updates);
    await reconcile();
  }

  function linkedUpdates(cart, baseKey, desiredQuantity) {
    const baseItem = cart.items.find((item) => item.key === baseKey);
    if (!baseItem || isServiceItem(baseItem)) return { [baseKey]: desiredQuantity };

    const updates = { [baseItem.key]: desiredQuantity };
    SERVICE_LINK_KEYS.forEach((key) => {
      const link = baseItem.properties?.[key];
      if (!link) return;
      cart.items.forEach((item) => {
        if (isServiceItem(item) && item.properties?.[key] === link) updates[item.key] = desiredQuantity;
      });
    });
    return updates;
  }

  async function refreshDrawer() {
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
      const drawer = getCartDrawer();
      if (!drawer) return false;

      const itemsRoot = drawer.querySelector('.js-items');
      const footerRoot = drawer.querySelector('.js-footer');

      try {
        const [itemsRes, footerRes, cart] = await Promise.all([
          fetch(`${url('cart')}?section_id=quick-cart&_=${Date.now()}`, { credentials: 'same-origin', cache: 'no-store' }),
          fetch(`${url('cart')}?section_id=quick-cart-footer&_=${Date.now()}`, { credentials: 'same-origin', cache: 'no-store' }),
          getCartJson()
        ]);

        if (itemsRes.ok && itemsRoot) {
          const holder = document.createElement('div');
          holder.innerHTML = await itemsRes.text();
          const items = holder.querySelector('[data-quick-cart-items]');
          if (items && itemsRoot.innerHTML !== items.innerHTML) itemsRoot.innerHTML = items.innerHTML;
        }

        if (footerRes.ok && footerRoot) {
          const holder = document.createElement('div');
          holder.innerHTML = await footerRes.text();
          const footer = holder.querySelector('[data-quick-cart-footer]');
          const nextFooterHtml = footer ? footer.innerHTML : '';
          if (footerRoot.innerHTML !== nextFooterHtml) footerRoot.innerHTML = nextFooterHtml;
        }

        updateCartCount(cart);
const title = drawer.querySelector('.quick-cart__title');
if (title && title.dataset.cartTitle) {
  title.textContent = title.dataset.cartTitle;
}
return true;
      } catch (error) {
        console.error('[UNBEGUN Cart] Drawer refresh failed', error);
        return false;
      } finally {
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  }

  async function reconcile() {
    if (reconcilePromise) return reconcilePromise;

    reconcilePromise = (async () => {
      // Linked service modules may repair an old or incomplete cart, but they
      // never render the drawer themselves. Run at most two passes so a repair
      // can settle without creating a feedback loop.
      for (let pass = 0; pass < 2; pass += 1) {
        let changed = false;
        if (window.UnbegunPersonalizer?.reconcile) changed = Boolean(await window.UnbegunPersonalizer.reconcile()) || changed;
        if (window.UnbegunGiftWrap?.reconcile) changed = Boolean(await window.UnbegunGiftWrap.reconcile()) || changed;
        if (window.UnbegunIpadKeyboard?.reconcile) changed = Boolean(await window.UnbegunIpadKeyboard.reconcile()) || changed;
        if (!changed) break;
      }
    })().finally(() => {
      reconcilePromise = null;
    });

    return reconcilePromise;
  }

  async function reconcileAndRefresh() {
    await reconcile();
    await refreshDrawer();
    window.UnbegunPersonalizer?.lockServiceControls?.();
  }

  async function changeBaseQuantity(baseKey, desiredQuantity, { reload = false } = {}) {
    if (cartActionRunning) return;
    cartActionRunning = true;
    try {
      const cart = await getCartJson();
      const updates = linkedUpdates(cart, baseKey, Math.max(0, desiredQuantity));
      await postCartUpdate(updates);
      await reconcile();
      if (reload) window.location.reload();
      else await refreshDrawer();
    } catch (error) {
      console.error('[UNBEGUN Cart] Quantity update failed', error);
    } finally {
      cartActionRunning = false;
    }
  }

  async function openDrawer() {
    const drawer = getCartDrawer();
    if (!drawer) {
      window.location.assign(url('cart'));
      return;
    }
    drawer.classList.add('is-active');
    document.documentElement.classList.add('unbegun-cart-open');
    await reconcileAndRefresh();
    requestAnimationFrame(() => drawer.classList.add('is-visible'));
  }

  const OPEN_AFTER_RELOAD_KEY = 'unbegun-open-cart-after-reload';

  // Multi-line transactions (base product + services) can race Horizon's
  // partial quick-cart renderer. A full reload gives Horizon one authoritative
  // cart state. The drawer is then opened through the exact same cart-icon flow
  // a customer normally uses.
  async function openAfterMutation() {
    try {
      await reconcile();
      sessionStorage.setItem(OPEN_AFTER_RELOAD_KEY, '1');
      window.location.reload();
      return true;
    } catch (error) {
      console.error('[UNBEGUN Cart] Reload cart open failed', error);
      window.location.assign(url('cart'));
      return false;
    }
  }

  function openRequestedDrawerAfterReload() {
    if (sessionStorage.getItem(OPEN_AFTER_RELOAD_KEY) !== '1') return;
    sessionStorage.removeItem(OPEN_AFTER_RELOAD_KEY);

    // Let Horizon and all section scripts finish their first render before
    // invoking the normal cart-button interaction.
    window.setTimeout(() => {
      const toggle = document.querySelector('.js-cart-drawer-toggle');
      if (toggle) toggle.click();
      else openDrawer();
    }, 180);
  }

  function closeDrawer() {
    const drawer = getCartDrawer();
    if (!drawer) return;
    drawer.classList.remove('is-visible');
    document.documentElement.classList.remove('unbegun-cart-open');
    window.setTimeout(() => drawer.classList.remove('is-active'), 300);
  }


  document.addEventListener('click', async (event) => {
    const cartToggle = event.target.closest('.js-cart-drawer-toggle');
    if (cartToggle) {
      event.preventDefault();
      event.stopImmediatePropagation();
      openDrawer();
      return;
    }

    const close = event.target.closest('[data-quick-cart] .js-close, [data-quick-cart] .js-overlay');
    if (close) {
      event.preventDefault();
      event.stopImmediatePropagation();
      closeDrawer();
      return;
    }

    const checkout = event.target.closest('[data-quick-cart] .quick-cart__checkout-button');
    if (checkout) {
      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.assign(url('checkout'));
      return;
    }

    const viewCart = event.target.closest('[data-quick-cart] .quick-cart__view-cart-button');
    if (viewCart) {
      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.assign(url('cart'));
      return;
    }

    const upsellArrow = event.target.closest('[data-quick-cart] .js-unbegun-upsell-prev, [data-quick-cart] .js-unbegun-upsell-next');
    if (upsellArrow) {
      event.preventDefault();
      const track = upsellArrow.closest('.unbegun-cart-upsells')?.querySelector('[data-unbegun-upsell-track]');
      if (track) {
        const direction = upsellArrow.classList.contains('js-unbegun-upsell-next') ? 1 : -1;
        track.scrollBy({ left: direction * Math.max(155, track.clientWidth * 0.72), behavior: 'smooth' });
      }
      return;
    }

    const quantityButton = event.target.closest('[data-quick-cart] .js-add-single, [data-quick-cart] .js-remove-single');
    if (quantityButton) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const itemRoot = quantityButton.closest('[data-cart-item]');
      if (!itemRoot || itemRoot.dataset.personalizationService === 'true') return;
      const key = itemRoot.dataset.itemKey;
      const current = Number(itemRoot.querySelector('.js-single-quantity')?.textContent || 0);
      const delta = quantityButton.classList.contains('js-add-single') ? 1 : -1;
      await changeBaseQuantity(key, current + delta);
      return;
    }

    const removeCartItem = event.target.closest('[data-unbegun-remove-item]');
    if (removeCartItem) {
      event.preventDefault();
      event.stopImmediatePropagation();
      await changeBaseQuantity(removeCartItem.dataset.itemKey, 0, { reload: true });
      return;
    }

    const addTrigger = event.target.closest('[data-add-button], button[name="add"], .product-form__submit');
    if (addTrigger) {
      const productForm = addTrigger.closest('form[data-product-form], form[action*="/cart/add"]');
      const optionTransaction = Boolean(
        productForm?.querySelector('[data-personalizer-toggle]:checked') ||
        productForm?.querySelector('[data-gift-wrap-toggle]:checked')
      );

      // Personalization and gift wrap submit all related lines themselves and
      // explicitly open the drawer only after the transaction is complete.
      // Do not start the native delayed refresh in parallel.
      if (optionTransaction) return;

      window.setTimeout(async () => {
        await reconcileAndRefresh();
        const drawer = getCartDrawer();
        if (drawer?.classList.contains('is-active')) drawer.classList.add('is-visible');
      }, 650);
    }
  }, true);

  function syncCartPageServiceInputs(form) {
    if (!form) return;
    const desiredByLink = new Map();
    const serviceRowsByLink = new Map();

    form.querySelectorAll('.cart-template__item[data-item-key]').forEach((row) => {
      const input = row.querySelector('input[name="updates[]"]');
      if (!input) return;
      const isService = row.dataset.personalizationService === 'true';
      const links = [row.dataset.personalizationLink, row.dataset.giftLink].filter(Boolean);

      links.forEach((link) => {
        if (isService) {
          if (!serviceRowsByLink.has(link)) serviceRowsByLink.set(link, []);
          serviceRowsByLink.get(link).push({ row, input });
        } else {
          const quantity = Math.max(0, Number(input.value || 0));
          desiredByLink.set(link, (desiredByLink.get(link) || 0) + quantity);
        }
      });
    });

    serviceRowsByLink.forEach((rows, link) => {
      const desired = desiredByLink.get(link) || 0;
      rows.forEach(({ input }, index) => { input.value = String(index === 0 ? desired : 0); });
    });
  }

  function cartPageFormFromTarget(target) {
    const form = target?.closest?.('[data-section-type="cart"] form, form[action*="/cart"]');
    if (!form || !form.querySelector('.cart-template__item[data-item-key]')) return null;
    return form;
  }

  // Keep the hidden service quantities synchronized *before* Shopify or the
  // theme serializes the cart form. This deliberately does not prevent the
  // native cart submission. Sleeve and service are therefore saved together
  // in one authoritative request, avoiding the former 2 → 1 race condition.
  document.addEventListener('input', (event) => {
    const form = cartPageFormFromTarget(event.target);
    if (!form || !event.target.matches('input[name="updates[]"]')) return;
    syncCartPageServiceInputs(form);
  }, true);

  document.addEventListener('change', (event) => {
    const form = cartPageFormFromTarget(event.target);
    if (!form || !event.target.matches('input[name="updates[]"]')) return;
    syncCartPageServiceInputs(form);
  }, true);

  document.addEventListener('click', (event) => {
    const updateButton = event.target.closest('[data-update-cart], input[name="update"], button[name="update"]');
    if (!updateButton) return;
    const form = cartPageFormFromTarget(updateButton);
    if (form) syncCartPageServiceInputs(form);
  }, true);

  document.addEventListener('submit', async (event) => {
    const upsellForm = event.target.closest?.('[data-unbegun-upsell-form]');
    if (!upsellForm) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (cartActionRunning) return;

    const button = upsellForm.querySelector('button[type="submit"]');
    cartActionRunning = true;
    button?.setAttribute('disabled', 'disabled');

    try {
      const response = await fetch(url('cart/add.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: new FormData(upsellForm)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result?.description || 'Product toevoegen is niet gelukt.');
      await reconcileAndRefresh();
    } catch (error) {
      console.error('[UNBEGUN Cart] Upsell add failed', error);
    } finally {
      cartActionRunning = false;
      button?.removeAttribute('disabled');
    }
  }, true);

  document.addEventListener('submit', (event) => {
    const form = cartPageFormFromTarget(event.target);
    if (!form) return;
    syncCartPageServiceInputs(form);
  }, true);

  document.addEventListener('input', (event) => {
    const note = event.target.closest?.('[data-unbegun-cart-note]');
    if (!note) return;

    const checkoutNote = getCartDrawer()?.querySelector('[data-unbegun-checkout-note]');
    if (checkoutNote) checkoutNote.value = note.value;

    const status = getCartDrawer()?.querySelector('[data-unbegun-note-status]');
    if (status) status.textContent = '';

    window.clearTimeout(noteSaveTimer);
    noteSaveTimer = window.setTimeout(async () => {
      try {
        await postCartUpdate({}, note.value);
        if (status) status.textContent = '✓';
        window.setTimeout(() => { if (status) status.textContent = ''; }, 1600);
      } catch (error) {
        console.error('[UNBEGUN Cart] Note save failed', error);
      }
    }, 450);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeDrawer();
  });

  document.addEventListener('DOMContentLoaded', async () => {
    const drawer = getCartDrawer();
    if (drawer) {
      drawer.querySelector('.quick-cart__close')?.setAttribute('type', 'button');
    }
    try { updateCartCount(await getCartJson()); } catch (_) {}
    openRequestedDrawerAfterReload();
  });

  window.UnbegunCart = {
    open: openDrawer,
    openAfterMutation,
    close: closeDrawer,
    refresh: refreshDrawer,
    reconcile: reconcileAndRefresh,
    changeQuantity: changeBaseQuantity
  };
})();